/**
 * API utilities for interacting with external storage service
 */

import { notifications } from '@mantine/notifications';

export const DIGI_STORAGE_API_BASE = 'https://storage.rcs-rds.ro';

export interface ImageFile {
  name: string;
  link: string;
}

export interface StorageApiMessages {
  failedToAuthenticate?: string;
  invalidResponseTokenNotFound?: string;
  networkError?: string;
  failedToGetMountId?: string;
  noMountsFound?: string;
  failedToListFolder?: string;
  folderNotFound?: string;
  failedToGetDownloadLink?: string;
  invalidResponseDownloadLinkNotFound?: string;
  failedToFetchImages?: string;
  failedToCreateFolder?: string;
  failedToCopyFile?: string;
  failedToRemoveFolder?: string;
}

export async function getToken(
  email: string,
  password: string,
  messages?: StorageApiMessages
): Promise<string> {
  try {
    const response = await fetch(`${DIGI_STORAGE_API_BASE}/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage =
        messages?.failedToAuthenticate ||
        `Failed to authenticate: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If error response is not JSON, use the text or default message
        if (errorText) {
          errorMessage = errorText;
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (!data.token) {
      throw new Error(
        messages?.invalidResponseTokenNotFound ||
          'Invalid response: token not found'
      );
    }
    return data.token;
  } catch (err: any) {
    if (err instanceof TypeError && err.message.includes('fetch')) {
      throw new Error(
        messages?.networkError ||
          'Network error: Unable to connect to DigiStorage'
      );
    }
    throw err;
  }
}

export async function deleteToken(token: string): Promise<void> {
  const response = await fetch(`${DIGI_STORAGE_API_BASE}/token`, {
    method: 'DELETE',
    headers: {
      Authorization: `Token ${token}`,
    },
  });

  if (!response.ok) {
    console.warn('Warning: failed to delete token');
  }
}

export async function getMountId(
  token: string,
  messages?: StorageApiMessages
): Promise<string> {
  try {
    const response = await fetch(`${DIGI_STORAGE_API_BASE}/api/v2/mounts`, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage =
        messages?.failedToGetMountId ||
        `Failed to get mount ID: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        if (errorText) {
          errorMessage = errorText;
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (
      !data.mounts ||
      !Array.isArray(data.mounts) ||
      data.mounts.length === 0
    ) {
      throw new Error(messages?.noMountsFound || 'No mounts found');
    }
    return data.mounts[0].id;
  } catch (err: any) {
    if (err instanceof TypeError && err.message.includes('fetch')) {
      throw new Error(
        messages?.networkError ||
          'Network error: Unable to connect to DigiStorage'
      );
    }
    throw err;
  }
}

export async function listFolder(
  token: string,
  mountId: string,
  path: string,
  messages?: StorageApiMessages
): Promise<any[]> {
  try {
    const url = `${DIGI_STORAGE_API_BASE}/api/v2/mounts/${mountId}/files/list?path=${encodeURIComponent(path)}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage =
        messages?.failedToListFolder ||
        `Failed to list folder: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        if (errorText) {
          errorMessage = errorText;
        }
      }
      // Handle 404 specifically for folder not found
      if (response.status === 404) {
        const folderNotFoundMsg =
          messages?.folderNotFound || `Folder not found: ${path}`;
        throw new Error(folderNotFoundMsg.replace('{path}', path));
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data.files || [];
  } catch (err: any) {
    if (err instanceof TypeError && err.message.includes('fetch')) {
      throw new Error(
        messages?.networkError ||
          'Network error: Unable to connect to DigiStorage'
      );
    }
    throw err;
  }
}

export async function getDownloadLink(
  token: string,
  mountId: string,
  filePath: string,
  messages?: StorageApiMessages
): Promise<string> {
  try {
    const url = `${DIGI_STORAGE_API_BASE}/api/v2/mounts/${mountId}/files/download?path=${encodeURIComponent(filePath)}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage =
        messages?.failedToGetDownloadLink ||
        `Failed to get download link: ${response.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        if (errorText) {
          errorMessage = errorText;
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (!data.link) {
      throw new Error(
        messages?.invalidResponseDownloadLinkNotFound ||
          'Invalid response: download link not found'
      );
    }
    return data.link;
  } catch (err: any) {
    if (err instanceof TypeError && err.message.includes('fetch')) {
      throw new Error(
        messages?.networkError ||
          'Network error: Unable to connect to DigiStorage'
      );
    }
    throw err;
  }
}

/**
 * Fetch images from folder using token (reuses token, doesn't delete it)
 * @param token - DigiStorage token
 * @param folderPath - Path to the folder
 * @returns Array of image files
 */
export async function fetchImagesFromFolderWithToken(
  token: string,
  folderPath: string,
  messages?: StorageApiMessages
): Promise<ImageFile[]> {
  const result: ImageFile[] = [];

  try {
    const mountId = await getMountId(token, messages);
    // Append /thumbnails to folderPath when importing
    const thumbnailsPath = `${folderPath}/thumbnails`;
    const files = await listFolder(token, mountId, thumbnailsPath, messages);

    // Process files with error handling for individual files
    for (const file of files) {
      if (file.type === 'file' && file.contentType?.startsWith('image/')) {
        try {
          const filePath = `${thumbnailsPath}/${file.name}`;
          const downloadLink = await getDownloadLink(
            token,
            mountId,
            filePath,
            messages
          );
          result.push({
            name: file.name,
            link: downloadLink,
          });
        } catch (fileError: any) {
          // Log error for individual file but continue processing others
          console.warn(
            `Failed to get download link for ${file.name}:`,
            fileError.message
          );
          // Optionally add file without link or skip it
          // For now, we'll skip files that fail to get download links
        }
      }
    }

    return result;
  } catch (err: any) {
    // Re-throw with original error message if it's already a meaningful error
    if (err.message && !err.message.includes('Network error')) {
      throw err;
    }
    // Otherwise, provide a more user-friendly message
    const errorMessage =
      err.message ||
      messages?.failedToFetchImages ||
      'Failed to fetch images from DigiStorage';
    throw new Error(errorMessage);
  }
}

/**
 * @deprecated Use fetchImagesFromFolderWithToken instead. This function creates and deletes a token.
 */
export async function fetchImagesFromFolder(
  email: string,
  password: string,
  folderPath: string,
  messages?: StorageApiMessages
): Promise<ImageFile[]> {
  let token: string | undefined;
  const result: ImageFile[] = [];

  try {
    token = await getToken(email, password, messages);
    const mountId = await getMountId(token, messages);
    // Append /thumbnails to folderPath when importing
    const thumbnailsPath = `${folderPath}/thumbnails`;
    const files = await listFolder(token, mountId, thumbnailsPath, messages);

    for (const file of files) {
      if (file.type === 'file' && file.contentType?.startsWith('image/')) {
        const filePath = `${thumbnailsPath}/${file.name}`;
        const downloadLink = await getDownloadLink(
          token,
          mountId,
          filePath,
          messages
        );
        result.push({
          name: file.name,
          link: downloadLink,
        });
      }
    }

    return result;
  } catch (err: any) {
    console.error('Error:', err.message);
    throw new Error(
      err.message || messages?.failedToFetchImages || 'Failed to fetch images'
    );
  } finally {
    if (token) {
      await deleteToken(token);
    }
  }
}

export async function copyFile(
  token: string,
  mountId: string,
  sourcePath: string,
  destinationPath: string,
  messages: any
) {
  try {
    const response = await fetch(
      `${DIGI_STORAGE_API_BASE}/api/v2/mounts/${mountId}/files/copy?path=${encodeURIComponent(sourcePath)}`,
      {
        method: 'PUT',
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          toMountId: mountId,
          toPath: destinationPath,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage =
        messages.filesCopiedErrorMessage || 'Failed to copy file';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        if (errorText) {
          errorMessage = errorText;
        }
      }
      throw new Error(errorMessage);
    }
  } catch (err: any) {
    const errorMessage =
      err.message || messages.filesCopiedErrorMessage || 'Failed to copy file';
    notifications.show({
      message: errorMessage,
      color: 'red',
      autoClose: 3000,
    });
    throw err;
  }
}

export async function deleteFolder(
  token: string,
  mountId: string,
  folderPath: string,
  messages: any
) {
  try {
    const response = await fetch(
      `${DIGI_STORAGE_API_BASE}/api/v2/mounts/${mountId}/files/remove?path=${encodeURIComponent(folderPath)}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage =
        messages.folderRemovedErrorMessage || 'Failed to remove folder';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        if (errorText) {
          errorMessage = errorText;
        }
      }
      throw new Error(errorMessage);
    }

    notifications.show({
      message: messages.folderRemovedMessage || 'Folder removed successfully',
      color: 'blue',
      autoClose: 3000,
    });
  } catch (err: any) {
    const errorMessage =
      err.message ||
      messages.folderRemovedErrorMessage ||
      'Failed to remove folder';
    notifications.show({
      message: errorMessage,
      color: 'red',
      autoClose: 3000,
    });
    throw err;
  }
}

export async function createFolder(
  token: string,
  mountId: string,
  parentPath: string,
  folderName: string,
  messages: any
) {
  try {
    const response = await fetch(
      `${DIGI_STORAGE_API_BASE}/api/v2/mounts/${mountId}/files/folder?path=${encodeURIComponent(parentPath)}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: folderName,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage =
        messages.folderCreatedErrorMessage || 'Failed to create folder';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        if (errorText) {
          errorMessage = errorText;
        }
      }
      throw new Error(errorMessage);
    }

    notifications.show({
      message: messages.folderCreatedMessage || 'Folder created successfully',
      color: 'green',
      autoClose: 3000,
    });
  } catch (err: any) {
    const errorMessage =
      err.message ||
      messages.folderCreatedErrorMessage ||
      'Failed to create folder';
    notifications.show({
      message: errorMessage,
      color: 'red',
      autoClose: 3000,
    });
    throw err;
  }
}

/**
 * Image Import and Organize Logic:
 *
 * Import (fetchImagesFromFolder):
 *   - Reads from {folderPath}/thumbnails
 *   - Images are imported from the thumbnails folder
 *
 * Organize (handleOrganizePhotosInDigiStorage):
 *   - Copies from {baseFolderPath}/{imageName} (base folder, not thumbnails)
 *   - Copies to {baseFolderPath}/{newFolderName}/{newImageName} (without /thumbnails)
 *
 * Flow:
 *   - Import: Reads from {folderPath}/thumbnails
 *   - Organize: Copies from {baseFolderPath}/{imageName} to {baseFolderPath}/{newFolderName}/{newImageName}
 */

export async function handleOrganizePhotosInDigiStorage(
  token: string,
  folderPath: string,
  newFolderName: string,
  imageNames: string[] | Array<{ originalName: string; newName: string }>,
  messages?: StorageApiMessages
): Promise<void> {
  try {
    // Use provided token (reuses existing token, doesn't delete it)
    const mountId = await getMountId(token, messages);

    // Remove /thumbnails from folderPath when organizing (if present)
    const organizeFolderPath = folderPath.replace(/\/thumbnails$/, '');
    const targetFolderPath = `${organizeFolderPath}/${newFolderName}`;

    // 2. Create folder with organizeFolderPath/newFolderName (without /thumbnails)
    const createResponse = await fetch(
      `${DIGI_STORAGE_API_BASE}/api/v2/mounts/${mountId}/files/folder?path=${encodeURIComponent(organizeFolderPath)}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newFolderName,
        }),
      }
    );

    if (!createResponse.ok) {
      const errorText = await createResponse.text();
      let errorMessage =
        messages?.failedToCreateFolder ||
        `Failed to create folder: ${createResponse.status}`;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        if (errorText) {
          errorMessage = errorText;
        }
      }
      throw new Error(errorMessage);
    }

    // 3. Copy files from baseFolderPath/image name to created folder (with optional renaming)
    // Source path should be from base folder (without /thumbnails), destination should not have /thumbnails
    const baseFolderPath = folderPath.replace(/\/thumbnails$/, '');
    for (const image of imageNames) {
      // Support both string array and object array with renaming
      const originalName =
        typeof image === 'string' ? image : image.originalName;
      const newName = typeof image === 'string' ? image : image.newName;

      const sourcePath = `${baseFolderPath}/${originalName}`;
      const destinationPath = `${targetFolderPath}/${newName}`;

      const copyResponse = await fetch(
        `${DIGI_STORAGE_API_BASE}/api/v2/mounts/${mountId}/files/copy?path=${encodeURIComponent(sourcePath)}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            toMountId: mountId,
            toPath: destinationPath,
          }),
        }
      );

      if (!copyResponse.ok) {
        const errorText = await copyResponse.text();
        let errorMessage =
          messages?.failedToCopyFile ||
          `Failed to copy file ${originalName}: ${copyResponse.status}`;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          if (errorText) {
            errorMessage = errorText;
          }
        }
        throw new Error(errorMessage);
      }
    }

    notifications.show({
      message: 'Photos organized successfully',
      color: 'green',
      autoClose: 3000,
    });
  } catch (err: any) {
    console.error('Error organizing photos:', err.message);
    notifications.show({
      message: err.message || 'Failed to organize photos',
      color: 'red',
      autoClose: 5000,
    });
    throw err;
  }
  // Note: Token is not deleted - it's reused from settings
}
